import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Alert,
} from '@mui/material';
import { GLYPHS } from '@/components/ui';
import { Employee } from '../../types/employee';
import { useEmployeeActions } from '../../hooks/useEmployeeActions';
import { useAccountBookStore } from '../../store/accountBookStore';
import { formatNumberWithCommas, parseNumberFromFormatted } from '../../utils/formatters';
import { HourlyRateUpdateType } from '../../types/hourlyRateHistory';

interface IncreaseHourlyRateBottomSheetProps {
  open: boolean;
  onClose: () => void;
  employee: Employee;
  onSuccess?: () => void;
}

const IncreaseHourlyRateBottomSheet: React.FC<IncreaseHourlyRateBottomSheetProps> = ({
  open,
  onClose,
  employee,
  onSuccess,
}) => {
  const { updateEmployee, loading } = useEmployeeActions();
  const { currentAccountBook } = useAccountBookStore();
  const [newRate, setNewRate] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (open && employee) {
      setNewRate(formatNumberWithCommas(employee.hourlyRate));
      setReason('');
      setError('');
      setConfirmOpen(false);
    }
  }, [open, employee]);

  const handleRateChange = (value: string) => {
    // 숫자와 콤마만 허용
    const cleaned = value.replace(/[^0-9,]/g, '');
    const numValue = parseNumberFromFormatted(cleaned);
    
    if (numValue >= 0) {
      setNewRate(formatNumberWithCommas(numValue));
      setError('');
    }
  };

  const handleSubmit = async () => {
    if (!employee.isHourly) {
      setError('시급제 직원만 시급을 변경할 수 있습니다.');
      return;
    }

    const parsedNewRate = parseNumberFromFormatted(newRate);
    
    if (parsedNewRate <= 0) {
      setError('시급은 0보다 큰 값이어야 합니다.');
      return;
    }

    if (parsedNewRate <= employee.hourlyRate) {
      setError('새 시급은 현재 시급보다 커야 합니다.');
      return;
    }

    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    try {
      const parsedNewRate = parseNumberFromFormatted(newRate);
      
      await updateEmployee(employee.id, {
        hourlyRate: parsedNewRate,
      }, {
        updateType: 'newOnly',
        reason: reason.trim() || undefined,
      });

      setConfirmOpen(false);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.message || '시급 변경에 실패했습니다.');
      setConfirmOpen(false);
    }
  };

  const handleCancel = () => {
    setConfirmOpen(false);
  };

  if (!employee.isHourly) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open && !confirmOpen}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              시급 올리기
            </Typography>
            <IconButton onClick={onClose} size="small" aria-label="닫기"
              sx={{ fontSize: 22, lineHeight: 1, color: 'var(--fg-secondary)' }}>
              {GLYPHS.close}
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>

          {/* 현재 시급 표시 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              현재 시급
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }} className="num">{formatNumberWithCommas(employee.hourlyRate)}</Typography>
            </Box>
          </Box>

          {/* 새 시급 입력 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              새 시급
            </Typography>
            <TextField
              fullWidth
              placeholder="시급을 입력하세요"
              value={newRate}
              onChange={(e) => handleRateChange(e.target.value)}
              error={!!error}
              helperText={error}
              sx={{ mb: 1 }}
            />
            {newRate && parseNumberFromFormatted(newRate) > employee.hourlyRate && (
              <Typography variant="caption" color="success.main">
                {formatNumberWithCommas(parseNumberFromFormatted(newRate) - employee.hourlyRate)} 인상
              </Typography>
            )}
          </Box>

          {/* 변경 사유 입력 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              변경 사유 (선택사항)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="변경 사유를 입력하세요"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Box>

          {/* 안내 메시지 */}
          <Alert severity="info" sx={{ mb: 3 }}>
            과거 스케줄의 급여는 이전 시급으로 계산됩니다. 새로 추가되는 스케줄부터 새 시급이 적용됩니다.
          </Alert>

          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={loading}
          >
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !newRate || parseNumberFromFormatted(newRate) <= employee.hourlyRate}
          >
            시급 올리기
          </Button>
        </DialogActions>
      </Dialog>

      {/* 확인 다이얼로그 */}
      <Dialog
        open={confirmOpen}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            시급 변경 확인
          </Typography>
        </DialogTitle>
        <DialogContent>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              직원명
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {employee.name}
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              시급 변경
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {formatNumberWithCommas(employee.hourlyRate)} → {newRate}
            </Typography>
          </Box>

          {reason && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                변경 사유
              </Typography>
              <Typography variant="body1">
                {reason}
              </Typography>
            </Box>
          )}

          <Alert severity="warning" sx={{ mb: 3 }}>
            과거 스케줄의 급여는 이전 시급으로 계산됩니다. 새로 추가되는 스케줄부터 새 시급이 적용됩니다.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={loading}
          >
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={loading}
          >
            확인
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IncreaseHourlyRateBottomSheet;

